import { useQuery } from "@tanstack/react-query";
import { getReportData, type ReportData } from "@/api/dpe";

/**
 * Affiche le rapport DPE estimé en React natif, avec la même mise en page
 * que les modèles officiels (documentation/modèles dpe).
 *
 * Sections (1 par "feuille A4") :
 *   1. Cover : identité du bien, étiquettes énergie + GES, coût annuel, CO₂
 *   2. Performance thermique : camembert déperditions + isolation par poste
 *   3. Coûts détaillés par usage + recommandations d'usage
 *   4. Équipements + enveloppe
 *   5. Recommandations travaux (scénarios sauvegardés ou recommandations génériques)
 *   6. Évolution attendue après travaux (si scénarios)
 *   7. Annexes techniques
 *
 * Bandeau "ESTIMATION SANS VALEUR LÉGALE" omniprésent (F-RAPP-003).
 * Aucun numéro ADEME — uniquement la référence interne EST- (F-RAPP-004).
 */
export function ReportView({ logementId }: { logementId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["report-data", logementId],
    queryFn: () => getReportData(logementId),
  });

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Chargement du rapport…</div>;
  }
  if (error || !data) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Impossible de charger le rapport.
      </div>
    );
  }

  return (
    <div className="bg-gray-100 p-4">
      <Banner />
      <CoverPage d={data} />
      <RentalWarningPage d={data} />
      <ThermalPage d={data} />
      <IndicatorsPage d={data} />
      <CostsPage d={data} />
      <DescriptionPage d={data} />
      <RecommendationsPage d={data} />
      {data.scenarios_detail.length > 0 && <EvolutionPage d={data} />}
      <AnnexPage d={data} />
    </div>
  );
}

// =============================================================================
// Page Indicateurs réglementaires (confort été + grading enveloppe)
// =============================================================================

function IndicatorsPage({ d }: { d: ReportData }) {
  const sc = d.summer_comfort;
  const grades = d.insulation_grades ?? [];
  const hasContent = sc?.classe || grades.length > 0;
  if (!hasContent) return null;

  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Indicateurs réglementaires du DPE
      </h2>

      {/* Confort d'été */}
      {sc?.classe && (
        <div className="mt-4">
          <h3 className="text-base font-bold text-blue-900">Confort d'été</h3>
          <p className="mt-1 text-xs text-gray-600">
            Évalue le risque de surchauffe estivale (canicules) sans
            climatisation. Inspecté par 4 critères : toiture, protections
            solaires extérieures S/E/O, inertie, traversance. Le verdict
            (Insuffisant / Moyen / Bon) suit la méthode officielle ;
            l'« indice /100 » est un repère indicatif, non réglementaire.
          </p>
          <div className="mt-3 flex items-center gap-4">
            <SummerComfortBadge classe={sc.classe} scoreContinu={sc.score_continu ?? null} />
            <div className="text-xs text-gray-700">
              <div>Toiture bien isolée : <strong>{sc.roof_well_insulated ? "Oui" : "Non"}</strong></div>
              <div>Protections solaires S/E/O : <strong>{sc.protected_facades_pct ?? 0} %</strong></div>
              <div>Logement traversant : <strong>{sc.is_traversant ? "Oui" : "Non"}</strong></div>
              <div>Inertie thermique : <strong>{sc.inertia_class ?? "—"}</strong></div>
            </div>
          </div>
          {sc.notes && sc.notes.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-gray-600">
              {sc.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Grading enveloppe */}
      {grades.length > 0 && (
        <div className="mt-6">
          <h3 className="text-base font-bold text-blue-900">
            Performance d'isolation par poste
          </h3>
          <p className="mt-1 text-xs text-gray-600">
            Note de performance pour chaque élément de l'enveloppe selon les
            seuils officiels (guide DPE p. 31). U exprimé en W/m²·K.
          </p>
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-gray-500">
              <tr>
                <th className="py-1 pr-2">Poste</th>
                <th className="py-1 pr-2">U effectif</th>
                <th className="py-1">Performance</th>
              </tr>
            </thead>
            <tbody>
              {grades.map((g) => (
                <tr key={g.poste} className="border-t border-gray-100">
                  <td className="py-1.5 pr-2">{g.poste_label}</td>
                  <td className="py-1.5 pr-2 tabular-nums">{g.u_value.toFixed(2)}</td>
                  <td className="py-1.5">
                    <span
                      className="rounded px-2 py-0.5 text-xs font-semibold text-white"
                      style={{ backgroundColor: g.grade_color }}
                    >
                      {g.grade_label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Sheet>
  );
}

// =============================================================================
// Conseils gratuits (sans travaux) — guide DPE Annexe 2 §III, §IV
//
// Affiché dans un onglet dédié « Améliorer sans travaux » de la page de saisie
// (cf. LogementDPE.tsx). N'est plus rendu inline dans <ReportView> (onglet
// « Simulation DPE + détails ») depuis le découpage en onglet séparé.
// =============================================================================

export function ConseilsSansTravaux({ d }: { d: ReportData }) {
  const usage = d.usage_tips ?? [];
  const maintenance = d.maintenance_tips ?? [];
  if (usage.length === 0 && maintenance.length === 0) return null;

  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Conseils gratuits — sans travaux
      </h2>
      <p className="mt-1 text-xs text-gray-600">
        Ces gestes simples permettent entre <strong>5 et 20 % d'économies</strong>
        sur votre facture sans investissement. Recommandés par le guide
        officiel DPE pour tout logement.
      </p>

      {usage.length > 0 && (
        <div className="mt-4">
          <h3 className="text-base font-bold text-blue-900">
            🌱 Bons gestes d'usage au quotidien
          </h3>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {usage.map((tip, i) => (
              <div key={i} className="rounded border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-start gap-2">
                  <span className="text-xl">{tip.icon}</span>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-emerald-900">
                      {tip.title}
                    </h4>
                    <p className="mt-1 text-xs text-emerald-800">{tip.description}</p>
                    {tip.saving_pct && (
                      <p className="mt-1 text-xs font-semibold text-emerald-700">
                        Économie estimée : {tip.saving_pct}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {maintenance.length > 0 && (
        <div className="mt-6">
          <h3 className="text-base font-bold text-blue-900">
            🔧 Entretien régulier des équipements
          </h3>
          <p className="mt-1 text-xs text-gray-600">
            Un équipement bien entretenu conserve son rendement et dure
            jusqu'à 30 % plus longtemps.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {maintenance.map((tip, i) => (
              <div key={i} className="rounded border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-start gap-2">
                  <span className="text-xl">{tip.icon}</span>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900">
                      {tip.title}
                      {tip.legal && (
                        <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                          Obligatoire
                        </span>
                      )}
                    </h4>
                    <p className="mt-1 text-xs text-blue-800">{tip.description}</p>
                    <p className="mt-1 text-xs font-semibold text-blue-700">
                      Fréquence : {tip.frequency}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Sheet>
  );
}

function SummerComfortBadge({
  classe,
  scoreContinu,
}: {
  classe: "bon" | "moyen" | "insuffisant";
  scoreContinu: number | null;
}) {
  const styles: Record<
    string,
    { bg: string; text: string; label: string; icon: string; stars: number }
  > = {
    bon: { bg: "#10b981", text: "white", label: "Bon", icon: "☀️", stars: 3 },
    moyen: { bg: "#f59e0b", text: "white", label: "Moyen", icon: "🌤️", stars: 2 },
    insuffisant: { bg: "#ef4444", text: "white", label: "Insuffisant", icon: "🥵", stars: 1 },
  };
  const s = styles[classe];
  return (
    <div
      className="flex flex-col items-center justify-center rounded p-3 text-center"
      style={{ backgroundColor: s.bg, color: s.text, minWidth: "110px" }}
    >
      <div className="text-2xl">{s.icon}</div>
      <div className="text-sm font-bold">{s.label}</div>
      <div className="text-sm" aria-label={`${s.stars} sur 3`}>
        {"★".repeat(s.stars)}
        {"☆".repeat(3 - s.stars)}
      </div>
      {scoreContinu != null && (
        <div className="text-[10px] opacity-90">Indice {scoreContinu}/100</div>
      )}
    </div>
  );
}

// =============================================================================
// Bandeau permanent
// =============================================================================

function Banner() {
  return (
    <div className="sticky top-0 z-10 mb-4 rounded border-2 border-amber-600 bg-amber-50 px-4 py-2 text-center text-sm font-semibold text-amber-900">
      ⚠️ ESTIMATION — Document sans valeur légale (pas un DPE réglementaire)
    </div>
  );
}

// =============================================================================
// Avertissement location (passoires énergétiques) — F/G uniquement
// =============================================================================

function RentalWarningPage({ d }: { d: ReportData }) {
  const label = (d.energy_label ?? "").toUpperCase();
  if (!["F", "G"].includes(label)) return null;

  const CALENDRIER: Record<string, string> = {
    G: "Les logements classés G sont interdits à la location depuis le 1ᵉʳ janvier 2023.",
    F: "Les logements classés F sont interdits à la location depuis le 1ᵉʳ janvier 2025.",
  };
  const next = label === "F" ? "E" : null;
  const nextInfo = next
    ? ` Les logements classés ${next} seront concernés à partir du 1ᵉʳ janvier 2028.`
    : "";

  return (
    <Sheet>
      <h2 className="border-b-2 border-red-700 pb-1 text-xl font-bold text-red-700">
        ⚠️ Obligations de location (passoires énergétiques)
      </h2>
      <div className="mt-3 rounded border-2 border-red-200 bg-red-50 p-4 text-sm text-red-900">
        <p>
          <strong>{CALENDRIER[label]}</strong>
          {nextInfo}
        </p>
        <p className="mt-2">
          Cette estimation classe votre logement en{" "}
          <strong>{label}</strong>. Cependant, <strong>cette estimation n'a
          pas de valeur légale</strong> et ne peut pas être opposée à
          l'administration. Seul un <strong>DPE officiel</strong> réalisé par
          un diagnostiqueur certifié détermine votre classe réglementaire et
          vos obligations de location.
        </p>
        <p className="mt-2">
          Avant toute décision, faites réaliser un DPE officiel par un
          diagnostiqueur certifié référencé sur{" "}
          <strong>diagnostiqueurs.din.developpement-durable.gouv.fr</strong>.
        </p>
      </div>
    </Sheet>
  );
}

// =============================================================================
// Helpers visuels
// =============================================================================

/** Carte "feuille A4" centrée pour chaque section du rapport. */
function Sheet({ children }: { children: React.ReactNode }) {
  return (
    <section className="lg-doc mx-auto mb-4 max-w-[200mm] rounded bg-white p-6 shadow-sm">
      {children}
    </section>
  );
}

/** Étiquette colorée A-G grand format. */
function LabelBox({
  letter,
  color,
  size = "md",
}: {
  letter: string;
  color: string;
  size?: "sm" | "md" | "lg";
}) {
  const sizes = {
    sm: "px-3 py-1 text-base",
    md: "px-4 py-2 text-2xl",
    lg: "px-6 py-4 text-5xl",
  };
  return (
    <span
      className={`inline-flex items-center justify-center rounded font-bold text-white ${sizes[size]}`}
      style={{ background: color, minWidth: size === "lg" ? "72px" : undefined }}
    >
      {letter}
    </span>
  );
}

/** Échelle A-G avec couleurs officielles. */
function Scale({ label }: { label?: string }) {
  const items: { letter: string; color: string }[] = [
    { letter: "A", color: "#0a8a3d" },
    { letter: "B", color: "#4cb050" },
    { letter: "C", color: "#c8d934" },
    { letter: "D", color: "#fdda1c" },
    { letter: "E", color: "#fb8c00" },
    { letter: "F", color: "#ed3d3d" },
    { letter: "G", color: "#c62828" },
  ];
  return (
    <div>
      <div className="flex h-5 overflow-hidden rounded text-xs font-bold text-white">
        {items.map((i) => (
          <div
            key={i.letter}
            className="flex flex-1 items-center justify-center"
            style={{ background: i.color, color: ["D", "E"].includes(i.letter) ? "#111" : "white" }}
          >
            {i.letter}
          </div>
        ))}
      </div>
      {label && <div className="mt-1 text-[10px] text-gray-500">{label}</div>}
    </div>
  );
}

// =============================================================================
// Page 1 : COVER
// =============================================================================

function CoverPage({ d }: { d: ReportData }) {
  return (
    <Sheet>
      <p className="text-xs text-gray-500">Estimation pédagogique — sans valeur légale</p>
      <h1 className="text-2xl font-bold text-blue-900">
        Simulation DPE — {d.logement_title}
      </h1>

      <table className="mt-3 w-full text-sm">
        <tbody>
          <Row label="Type de bien">
            {d.logement_type_label}{" "}
            <span className="text-gray-500">
              ({d.is_neuf ? "construction récente" : "existant"})
            </span>
          </Row>
          {(d.address_line || d.postal_code || d.city) && (
            <Row label="Adresse">
              {d.address_line}
              {d.postal_code ? ` — ${d.postal_code} ${d.city}` : ""}
            </Row>
          )}
          <Row label="Année de construction">{String(d.construction_year)}</Row>
          {d.shab_m2 && <Row label="Surface habitable">{d.shab_m2} m²</Row>}
          <Row label="Date d'estimation">{d.estimation_date}</Row>
          <Row label="Référence interne">
            {d.internal_reference}{" "}
            <span className="text-xs text-gray-500">(pas un n° ADEME)</span>
          </Row>
        </tbody>
      </table>

      <div className="my-4 rounded border-2 border-amber-600 bg-amber-50 p-3 text-sm">
        <div className="font-bold text-amber-900">{d.disclaimer_title}</div>
        <div className="mt-1 text-amber-900">{d.disclaimer_body}</div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Étiquette ÉNERGIE */}
        <div className="rounded border bg-gray-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-600">
            Consommation — énergie primaire
          </div>
          <div className="mt-3 flex items-center gap-4">
            <LabelBox letter={d.energy_label} color={d.energy_label_color} size="lg" />
            <div>
              <div className="text-2xl font-bold">{d.energy_kwh_per_m2}</div>
              <div className="text-xs text-gray-600">kWh ep / m² / an</div>
              {d.consumption_final_per_m2 !== "—" && (
                <div className="mt-1 text-xs text-gray-500">
                  soit {d.consumption_final_per_m2} kWh ef / m² / an
                </div>
              )}
            </div>
          </div>
          <div className="mt-3">
            <Scale />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-gray-500">
            <span>Logement très performant</span>
            <span>Passoire énergétique</span>
          </div>
        </div>

        {/* Étiquette GES */}
        <div className="rounded border bg-gray-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-600">
            Émissions de gaz à effet de serre
          </div>
          <div className="mt-3 flex items-center gap-4">
            <LabelBox letter={d.ges_label} color={d.ges_label_color} size="lg" />
            <div>
              <div className="text-2xl font-bold">{d.ges_kgco2_per_m2}</div>
              <div className="text-xs text-gray-600">kg CO₂ eq / m² / an</div>
              {d.co2_total_kg_per_year !== "—" && (
                <div className="mt-1 text-xs text-gray-500">
                  Soit {d.co2_total_kg_per_year} kg CO₂ / an
                  <br />≈ {d.co2_equiv_km} km en voiture
                </div>
              )}
            </div>
          </div>
          <div className="mt-3">
            <Scale />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-gray-500">
            <span>Peu d'émissions</span>
            <span>Émissions très importantes</span>
          </div>
        </div>
      </div>

      {d.annual_costs_total_min !== "—" && (
        <div className="mt-4 border-l-4 border-blue-900 bg-blue-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-600">
            Coût annuel estimé (énergie)
          </div>
          <div className="mt-1 text-2xl font-bold text-blue-900">
            entre {d.annual_costs_total_min} et {d.annual_costs_total_max} / an
          </div>
          <div className="mt-1 text-xs text-gray-600">
            Prix moyens 2021-2024, abonnement compris. Hors usages électroménager / multimédia.
          </div>
        </div>
      )}
    </Sheet>
  );
}

// =============================================================================
// Page 2 : Performance thermique
// =============================================================================

function ThermalPage({ d }: { d: ReportData }) {
  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Performance thermique du logement
      </h2>

      {d.losses_pct.length > 0 && (
        <>
          <h3 className="mt-4 text-base font-bold text-blue-900">
            Schéma des déperditions de chaleur
          </h3>
          <div className="mt-2 grid items-center gap-4 md:grid-cols-[180px_1fr]">
            <LossesPie slices={d.losses_pct} />
            <ul className="text-sm">
              {d.losses_pct.map((s, idx) => (
                <li key={s.key} className="flex items-center py-0.5">
                  <span
                    className="mr-2 inline-block h-3 w-3 rounded"
                    style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }}
                  />
                  <strong>{s.label}</strong>
                  <span className="ml-2 text-gray-600">
                    — {s.pct}% ({s.value_w_per_k} W/K)
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Les ponts thermiques sont estimés de façon forfaitaire en V1 (≈ 15 % des
            déperditions surfaciques). Un calcul détaillé par liaison sera disponible
            dans une version ultérieure.
          </p>
        </>
      )}

      <h3 className="mt-6 text-base font-bold text-blue-900">
        Performance de l'isolation par poste
      </h3>
      <div className="mt-2">
        {(["walls", "roof", "low_floor", "windows", "doors"] as const).map((post) => {
          const labels: Record<string, string> = {
            walls: "Murs",
            roof: "Toiture / plafond",
            low_floor: "Plancher bas",
            windows: "Fenêtres",
            doors: "Portes",
          };
          const level = d.insulation_perf[post] ?? "non évalué";
          return (
            <PerfRow
              key={post}
              postLabel={labels[post]}
              level={level as PerfLevel}
            />
          );
        })}
        <div className="ml-[120px] mr-[100px] mt-1 flex justify-between text-[10px] text-gray-500">
          <span>Insuffisante</span>
          <span>Moyenne</span>
          <span>Bonne</span>
          <span>Très bonne</span>
        </div>
      </div>

      <h3 className="mt-6 text-base font-bold text-blue-900">Ventilation en place</h3>
      <p className="text-sm">{d.equipment.ventilation_label}</p>

      <h3 className="mt-4 text-base font-bold text-blue-900">
        Production d'énergies renouvelables
      </h3>
      <p className="text-sm">
        {/heat\s?pump|pac|solaire|bois|granul/i.test(d.equipment.heating_label) ? (
          <>
            Le logement utilise au moins une source d'énergie partiellement renouvelable
            ({d.equipment.heating_label}).
          </>
        ) : (
          <>
            Le logement ne dispose pas (encore) de production d'énergie renouvelable.
            Solutions possibles : pompe à chaleur, panneaux solaires, chaudière bois,
            géothermie, réseau de chaleur vertueux.
          </>
        )}
      </p>
    </Sheet>
  );
}

const PIE_COLORS = ["#1e40af", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

function LossesPie({ slices }: { slices: ReportData["losses_pct"] }) {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <svg viewBox="0 0 140 140" className="h-44 w-44">
      {slices.map((s, idx) => {
        const length = (s.pct * circumference) / 100;
        const dasharray = `${length} ${circumference - length}`;
        const dashoffset = -offset;
        offset += length;
        return (
          <circle
            key={s.key}
            cx={70}
            cy={70}
            r={radius}
            fill="none"
            stroke={PIE_COLORS[idx % PIE_COLORS.length]}
            strokeWidth={35}
            strokeDasharray={dasharray}
            strokeDashoffset={dashoffset}
            transform="rotate(-90 70 70)"
          />
        );
      })}
      <circle cx={70} cy={70} r={22} fill="white" />
      <text x={70} y={75} textAnchor="middle" fontSize={11} fill="#0b3a8c" fontWeight={700}>
        100%
      </text>
    </svg>
  );
}

type PerfLevel = "insuffisante" | "moyenne" | "bonne" | "très bonne" | "non évalué";

function PerfRow({ postLabel, level }: { postLabel: string; level: PerfLevel }) {
  const positions: Record<PerfLevel, string> = {
    insuffisante: "8%",
    moyenne: "40%",
    bonne: "70%",
    "très bonne": "92%",
    "non évalué": "50%",
  };
  return (
    <div className="my-1.5 grid grid-cols-[120px_1fr_100px] items-center gap-2 text-sm">
      <div className="font-medium">{postLabel}</div>
      <div className="relative h-3 rounded bg-gradient-to-r from-red-500 via-yellow-300 to-green-600">
        <div
          className="absolute -top-0.5 h-4 w-1.5 rounded bg-gray-900"
          style={{ left: positions[level] }}
        />
      </div>
      <div className="text-xs capitalize">{level}</div>
    </div>
  );
}

// =============================================================================
// Page 3 : Coûts détaillés
// =============================================================================

function CostsPage({ d }: { d: ReportData }) {
  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Consommations et coûts annuels d'énergie
      </h2>

      <p className="mt-2 text-xs text-gray-500">
        Seuls le chauffage, la climatisation, l'eau chaude sanitaire, l'éclairage et
        les auxiliaires sont pris en compte. Les usages électroménager / multimédia ne
        sont pas comptabilisés. Prix moyens 2021-2024, abonnement compris.
      </p>

      {d.annual_costs_rows.length > 0 ? (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="py-2 text-left text-xs uppercase tracking-wide text-gray-600">Usage</th>
              <th className="py-2 text-left text-xs uppercase tracking-wide text-gray-600">Énergie</th>
              <th className="py-2 text-right text-xs uppercase tracking-wide text-gray-600">
                Conso (kWh ef / an)
              </th>
              <th className="py-2 text-right text-xs uppercase tracking-wide text-gray-600">
                Coût annuel estimé
              </th>
            </tr>
          </thead>
          <tbody>
            {d.annual_costs_rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="py-1.5">{row.use_label}</td>
                <td className="py-1.5">{row.energy_label}</td>
                <td className="py-1.5 text-right">{row.kwh_ef}</td>
                <td className="py-1.5 text-right">
                  entre {row.cost_min} et {row.cost_max}
                </td>
              </tr>
            ))}
            <tr className="border-t-2 font-semibold">
              <td className="py-2" colSpan={2}>
                Total
              </td>
              <td className="py-2 text-right">{d.consumption_final_total}</td>
              <td className="py-2 text-right">
                entre {d.annual_costs_total_min} et {d.annual_costs_total_max}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <p className="mt-2 text-xs text-gray-500">
          Pas d'information de coût disponible (consommation par usage non calculée).
        </p>
      )}

      <h3 className="mt-6 text-base font-bold text-blue-900">
        Quelques gestes simples pour maîtriser votre facture
      </h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
        <li>
          <strong>Chauffer à 19 °C plutôt que 21 °C</strong> peut réduire la facture
          de ~16 % ; baisser de 1 °C la nuit ajoute encore quelques %.
        </li>
        <li>
          <strong>Eau chaude</strong> : consigne entre 55 et 60 °C, mousseurs, pommeau
          économe, douches courtes.
        </li>
        <li>
          <strong>Éclairage</strong> : LED partout, éteindre en quittant une pièce.
        </li>
        <li>
          <strong>Ventilation</strong> : nettoyer les bouches d'extraction et grilles
          d'entrée régulièrement.
        </li>
        <li>
          <strong>Climatisation</strong> (si utilisée) : consigne à 26-28 °C, fermer
          les volets en journée.
        </li>
      </ul>
    </Sheet>
  );
}

// =============================================================================
// Page 4 : Équipements + enveloppe
// =============================================================================

function DescriptionPage({ d }: { d: ReportData }) {
  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Équipements & enveloppe
      </h2>

      <h3 className="mt-3 text-base font-bold text-blue-900">Vue d'ensemble des équipements</h3>
      <table className="mt-2 w-full text-sm">
        <tbody>
          <Row label="Chauffage">{d.equipment.heating_label}</Row>
          <Row label="Eau chaude sanitaire">{d.equipment.ecs_label}</Row>
          <Row label="Ventilation">{d.equipment.ventilation_label}</Row>
        </tbody>
      </table>

      <h3 className="mt-5 text-base font-bold text-blue-900">Vue d'ensemble de l'enveloppe</h3>

      <h4 className="mt-3 text-sm font-bold text-gray-700">Murs</h4>
      {d.envelope.walls.length > 0 ? (
        <ul className="mt-1 list-disc pl-5 text-sm">
          {d.envelope.walls.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500">Aucun mur saisi.</p>
      )}

      <h4 className="mt-3 text-sm font-bold text-gray-700">Toiture / plafond</h4>
      <p className="text-sm">{d.envelope.roof ?? "Non renseigné."}</p>

      <h4 className="mt-3 text-sm font-bold text-gray-700">Plancher bas</h4>
      <p className="text-sm">{d.envelope.low_floor ?? "Non renseigné."}</p>

      <h4 className="mt-3 text-sm font-bold text-gray-700">Fenêtres</h4>
      {d.envelope.windows.length > 0 ? (
        <ul className="mt-1 list-disc pl-5 text-sm">
          {d.envelope.windows.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500">Aucune fenêtre saisie.</p>
      )}

      <h4 className="mt-3 text-sm font-bold text-gray-700">Portes (donnant sur l'extérieur)</h4>
      {d.envelope.doors.length > 0 ? (
        <ul className="mt-1 list-disc pl-5 text-sm">
          {d.envelope.doors.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-500">Aucune porte saisie.</p>
      )}
    </Sheet>
  );
}

// =============================================================================
// Page 5 : Recommandations travaux
// =============================================================================

function RecommendationsPage({ d }: { d: ReportData }) {
  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Recommandations de travaux
      </h2>

      {d.scenarios_detail.length > 0 ? (
        <>
          <p className="mt-2 text-xs text-gray-500">
            Voici les scénarios de travaux que vous avez configurés. Coûts et aides
            sont des fourchettes indicatives — demandez plusieurs devis à des artisans
            certifiés RGE.
          </p>
          {d.scenarios_detail.map((s, i) => (
            <ScenarioCard key={i} s={s} />
          ))}
        </>
      ) : d.auto_recommendations && d.auto_recommendations.length > 0 ? (
        <>
          <p className="mt-2 text-xs text-gray-500">
            Recommandations chiffrées automatiques pour votre logement, classées par
            rentabilité décroissante. Toutes ont un retour sur investissement{" "}
            <strong>inférieur à 20 ans</strong> et un gain énergétique{" "}
            <strong>supérieur à 5 %</strong> (relevance "excellent" ou "bon"). Coûts
            et aides sont des fourchettes indicatives — demandez plusieurs devis à
            des artisans certifiés RGE.
          </p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-gray-500">
                <tr>
                  <th className="py-1 pr-2 font-medium">Amélioration</th>
                  <th className="py-1 pr-2 font-medium">Étiquette</th>
                  <th className="py-1 pr-2 font-medium text-right">ROI</th>
                  <th className="py-1 pr-2 font-medium text-right">€ / an</th>
                  <th className="py-1 pr-2 font-medium text-right">Coût</th>
                  <th className="py-1 font-medium text-right">Aides</th>
                </tr>
              </thead>
              <tbody>
                {d.auto_recommendations.map((r) => (
                  <tr key={r.key} className="border-t border-gray-100">
                    <td className="py-1.5 pr-2">
                      <span className="font-medium">{r.label}</span>
                      {r.description && (
                        <span className="block text-[10px] text-gray-500">
                          {r.description}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      <span className="text-gray-500">{r.label_before}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="font-semibold text-emerald-700">
                        {r.label_after}
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums">
                      <span
                        className={
                          r.roi_years < 10
                            ? "font-semibold text-emerald-700"
                            : "text-gray-700"
                        }
                      >
                        {r.roi_years} ans
                      </span>
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-emerald-700">
                      {r.annual_eur_saved > 0
                        ? `${Math.round(r.annual_eur_saved).toLocaleString("fr-FR")} €`
                        : "—"}
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                      {r.cost_min_eur.toLocaleString("fr-FR")} – {r.cost_max_eur.toLocaleString("fr-FR")} €
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-blue-700">
                      {r.aids_total_eur > 0
                        ? `${Math.round(r.aids_total_eur).toLocaleString("fr-FR")} €`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Pour une simulation détaillée d'un bouquet (combinaison de plusieurs
            gestes), créez un scénario dans la section "Scénarios" plus bas.
          </p>
        </>
      ) : d.actions.length > 0 ? (
        <>
          <p className="mt-2 text-xs text-gray-500">
            Aucune amélioration standard n'a un ROI inférieur à 20 ans pour ce
            logement. Voici à titre indicatif les postes les plus déperditifs :
          </p>
          <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm">
            {d.actions.map((a, i) => (
              <li key={i}>
                <strong>{a.poste}</strong> ({a.deperdition_w_per_k} W/K) —{" "}
                {a.suggestion}
              </li>
            ))}
          </ol>
        </>
      ) : (
        <p className="text-sm">Aucune recommandation prioritaire identifiable.</p>
      )}

      <div className="mt-4 rounded border-2 border-amber-600 bg-amber-50 p-3 text-sm">
        <div className="font-bold text-amber-900">
          Préparez votre projet avec un conseiller France Rénov'
        </div>
        <div className="mt-1 text-amber-900">
          Conseils gratuits et indépendants :{" "}
          <strong>france-renov.gouv.fr</strong> ou 0808 800 700. Simulation des
          aides (MaPrimeRénov', CEE, éco-PTZ) sur <strong>france-renov.gouv.fr/aides</strong>.
        </div>
      </div>
    </Sheet>
  );
}

const RELEVANCE_STYLES: Record<
  string,
  { border: string; bg: string; text: string; icon: string; title: string }
> = {
  excellent: {
    border: "border-emerald-600",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    icon: "✅",
    title: "Très bon investissement",
  },
  bon: {
    border: "border-emerald-400",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    icon: "👍",
    title: "Bon investissement",
  },
  ok: {
    border: "border-blue-900",
    bg: "bg-white",
    text: "text-blue-900",
    icon: "",
    title: "",
  },
  marginal: {
    border: "border-amber-500",
    bg: "bg-amber-50",
    text: "text-amber-900",
    icon: "ℹ️",
    title: "Gain limité",
  },
  non_rentable: {
    border: "border-orange-500",
    bg: "bg-orange-50",
    text: "text-orange-900",
    icon: "💰",
    title: "Non rentable financièrement",
  },
  inadapte: {
    border: "border-red-500",
    bg: "bg-red-50",
    text: "text-red-900",
    icon: "⚠️",
    title: "Scénario inadapté à ce logement",
  },
};

const TIER_LABEL_SHORT: Record<string, string> = {
  bleu: "Bleu (très modestes)",
  jaune: "Jaune (modestes)",
  violet: "Violet (intermédiaires)",
  rose: "Rose (supérieurs)",
};

function ScenarioCard({ s }: { s: ReportData["scenarios_detail"][number] }) {
  const style = RELEVANCE_STYLES[s.relevance] ?? RELEVANCE_STYLES.ok;
  return (
    <div className={`mt-3 rounded border-2 p-3 ${style.border} ${style.bg}`}>
      <h3 className={`font-bold ${style.text}`}>{s.name}</h3>

      {/* Bandeau de pertinence quand applicable */}
      {s.relevance !== "ok" && (
        <div className={`mt-2 rounded p-2 text-xs ${style.text}`}>
          <strong>
            {style.icon} {style.title}.
          </strong>{" "}
          {s.relevance_message ?? ""}
        </div>
      )}

      {s.improvements.length > 0 && (
        <>
          <p className="mt-2 text-sm font-semibold">Travaux inclus :</p>
          <ul className="list-disc pl-5 text-sm">
            {s.improvements.map((imp, i) => (
              <li key={i}>{imp}</li>
            ))}
          </ul>
        </>
      )}

      {/* Comparaison avant / après — étiquettes énergie + GES */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-1">
          <span className="text-gray-600">Avant :</span>
          <LabelBox letter={s.label_before} color={s.label_before_color} size="sm" />
          <span>{s.primary_per_m2_before} kWh ep/m²</span>
        </div>
        <span className="text-gray-400">→</span>
        <div className="flex items-center gap-1">
          <span className="text-gray-600">Après :</span>
          <LabelBox letter={s.new_energy_label} color={s.new_energy_color} size="sm" />
          <span>{s.new_consumption_per_m2} kWh ep/m²</span>
        </div>
      </div>

      <table className="mt-2 w-full text-sm">
        <tbody>
          {s.annual_eur_saved !== "—" && Number(s.annual_eur_saved) > 0 && (
            <Row label="Économie annuelle">
              ~{Math.round(Number(s.annual_eur_saved)).toLocaleString("fr-FR")} € / an
            </Row>
          )}
          <Row label="Coût estimé">
            entre {s.cost_min} et {s.cost_max}
          </Row>
          {s.aids_total !== "—" && (
            <Row label="Aides estimées">
              {s.aids_total}
              {s.tier && (
                <span className="text-xs text-gray-600">
                  {" "}— tier MPR {TIER_LABEL_SHORT[s.tier] ?? s.tier}
                </span>
              )}
              {s.aids_mpr !== "—" && s.aids_cee !== "—" && (
                <span className="text-xs text-gray-500">
                  {" "}(MPR {s.aids_mpr} + CEE {s.aids_cee})
                </span>
              )}
            </Row>
          )}
          {s.ampleur_eligible && s.accompagnateur_aid_eur && (
            <Row label="Accompagnateur Rénov'">
              Forfait 2 000 € pris en charge à hauteur de {s.accompagnateur_aid_eur}
            </Row>
          )}
          {s.roi_years !== "—" ? (
            <Row label="Retour sur investissement">~ {s.roi_years} ans (estimatif)</Row>
          ) : (
            <Row label="Retour sur investissement">
              <span className="text-gray-500">non calculable (gain trop faible)</span>
            </Row>
          )}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Page 6 : Évolution après travaux
// =============================================================================

function EvolutionPage({ d }: { d: ReportData }) {
  // On filtre les scénarios inadaptés/non rentables pour l'affichage
  // d'évolution : l'utilisateur ne veut pas voir un "après" qui dégrade
  // ou ne se rentabilise jamais comparé à l'actuel. Les niveaux
  // "excellent" et "bon" sont évidemment inclus.
  const relevantScenarios = d.scenarios_detail.filter((s) =>
    ["excellent", "bon", "ok", "marginal"].includes(s.relevance),
  );
  const filteredOut = d.scenarios_detail.length - relevantScenarios.length;

  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Évolution de la performance après travaux
      </h2>
      <p className="mt-2 text-xs text-gray-500">
        Comparaison des étiquettes énergie et GES entre la situation actuelle
        et les scénarios pertinents.
        {filteredOut > 0 && (
          <>
            {" "}({filteredOut} scénario{filteredOut > 1 ? "s" : ""} masqué
            {filteredOut > 1 ? "s" : ""} car inadapté{filteredOut > 1 ? "s" : ""}{" "}
            ou non rentable{filteredOut > 1 ? "s" : ""}.)
          </>
        )}
      </p>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <CompareCard
          title="Situation actuelle"
          energyLabel={d.energy_label}
          energyColor={d.energy_label_color}
          gesLabel={d.ges_label}
          gesColor={d.ges_label_color}
          consoPerM2={d.consumption_primary_per_m2}
          gesPerM2={d.ges_kgco2_per_m2}
        />
        {relevantScenarios.map((s, i) => (
          <CompareCard
            key={i}
            title={`Après "${s.name}"`}
            energyLabel={s.new_energy_label}
            energyColor={s.new_energy_color}
            gesLabel={s.new_ges_label}
            gesColor={s.new_ges_color}
            consoPerM2={s.new_consumption_per_m2}
            gesPerM2={s.new_ges_per_m2}
          />
        ))}
      </div>

      {relevantScenarios.length === 0 && d.scenarios_detail.length > 0 && (
        <p className="mt-4 rounded bg-gray-50 p-3 text-xs text-gray-600">
          Aucun de vos scénarios n'apparaît rentable / pertinent pour ce
          logement (voir la page Recommandations pour le détail).{" "}
          <strong>
            Sur l'horizon usuel d'un investissement immobilier (20 ans), le
            coût des travaux n'est pas couvert par les économies d'énergie
            attendues.
          </strong>{" "}
          Cela n'empêche pas de les réaliser pour le confort, la valeur de
          revente, ou en remplacement d'un équipement en fin de vie —
          simplement le retour sur investissement purement énergétique est
          limité.
        </p>
      )}
    </Sheet>
  );
}

function CompareCard({
  title,
  energyLabel,
  energyColor,
  gesLabel,
  gesColor,
  consoPerM2,
  gesPerM2,
}: {
  title: string;
  energyLabel: string;
  energyColor: string;
  gesLabel: string;
  gesColor: string;
  consoPerM2: number | string;
  gesPerM2: number | string;
}) {
  return (
    <div className="rounded border p-3 text-center">
      <div className="text-xs font-bold uppercase tracking-wide text-gray-600">{title}</div>
      <div className="mt-2 flex items-center justify-center gap-2">
        <LabelBox letter={energyLabel} color={energyColor} size="md" />
        <LabelBox letter={gesLabel} color={gesColor} size="md" />
      </div>
      <div className="mt-2 text-xs text-gray-600">
        {consoPerM2} kWh ep / m² / an
        <br />
        {gesPerM2} kg CO₂ / m² / an
      </div>
    </div>
  );
}

// =============================================================================
// Page 7 : Annexes techniques
// =============================================================================

function AnnexPage({ d }: { d: ReportData }) {
  return (
    <Sheet>
      <h2 className="border-b-2 border-blue-900 pb-1 text-xl font-bold text-blue-900">
        Annexes — Données techniques
      </h2>

      <h3 className="mt-3 text-base font-bold text-blue-900">Climat et localisation</h3>
      <table className="mt-2 w-full text-sm">
        <tbody>
          <Row label="Zone climatique">{d.climate_zone}</Row>
          <Row label="Degrés-jours unifiés (DJU)">{String(d.climate_dju)}</Row>
          {d.postal_code && (
            <Row label="Code postal">
              {d.postal_code} {d.city}
            </Row>
          )}
        </tbody>
      </table>

      <h3 className="mt-4 text-base font-bold text-blue-900">
        Déperditions thermiques détaillées
      </h3>
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="py-1.5 text-left text-xs uppercase tracking-wide text-gray-600">Poste</th>
            <th className="py-1.5 text-right text-xs uppercase tracking-wide text-gray-600">
              Déperdition (W/K)
            </th>
          </tr>
        </thead>
        <tbody>
          {d.losses_rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1">{row.label}</td>
              <td className="py-1 text-right">{String(row.value)}</td>
            </tr>
          ))}
          <tr className="border-t-2 font-semibold">
            <td className="py-1.5">Total</td>
            <td className="py-1.5 text-right">{String(d.losses_total)}</td>
          </tr>
        </tbody>
      </table>

      {d.warnings.length > 0 && (
        <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3">
          <div className="font-bold text-amber-900">Avertissements de saisie</div>
          <ul className="mt-1 list-disc pl-5 text-sm text-amber-900">
            {d.warnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-6 text-xs text-gray-500">
        Moteur 3CL-DPE v{d.engine_version} · schéma payload v{d.schema_version} ·
        Référence interne : {d.internal_reference}
        <br />
        Cette estimation n'est pas un DPE réglementaire. Pour un DPE officiel, faire
        appel à un diagnostiqueur certifié référencé sur
        diagnostiqueurs.din.developpement-durable.gouv.fr.
      </p>
    </Sheet>
  );
}

// =============================================================================
// Petit helper de ligne tableau
// =============================================================================

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="w-1/3 py-1.5 font-semibold text-gray-700">{label}</td>
      <td className="py-1.5">{children}</td>
    </tr>
  );
}
